import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export type ChipOption<V extends string | number> = {
  value: V;
  label: string;
  hint?: string;
};

export function ChipSelect<V extends string | number>({
  options,
  value,
  onChange,
  className,
}: {
  options: ChipOption<V>[];
  value: V | undefined;
  onChange: (next: V) => void;
  className?: string;
}) {
  const { t, i18n } = useTranslation('intake');
  const isJapanese = i18n.language?.startsWith('ja');

  const labelFor = (opt: ChipOption<V>) => {
    if (typeof opt.value === 'number') return opt.label;
    return t(`optionLabels.${opt.value}`, {
      defaultValue: isJapanese ? opt.label : humanizeOptionValue(opt.value),
    });
  };

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {options.map((opt) => {
        const active = opt.value === value;
        const label = labelFor(opt);
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs transition',
              active
                ? 'border-synergy-blue bg-synergy-blue/10 text-synergy-blue'
                : 'border-synergy-neutral-700/40 bg-white/40 text-synergy-text-tertiary hover:border-synergy-blue/30',
            )}
            title={opt.hint}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function humanizeOptionValue(value: string): string {
  if (/^M\d/.test(value) || /^IP\d/.test(value) || value === value.toUpperCase()) {
    return value.replace(/_/g, ' ');
  }
  return value
    .replace(/^screw_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
